import { __decorate } from "tslib";
/**
 * Module List Renderless Component using Slot Scope
 * https://adamwathan.me/renderless-components-in-vuejs/
 * Need to define `render()` function which returns slots
 */
import { mixins } from 'vue-class-component';
import { Component, Prop } from 'vue-property-decorator';
import PaginatorMixin from '@/helpers/mixins/PaginatorMixin';
import ConfirmationDialog from '@/components/ConfirmationDialog.vue';
import FormMixin from '@/helpers/vue_mixins/FormMixin';
import EcommerceAppProducts from '@/plugins/graphql/queries/v2/ecommerce/products.gql';
import AppSalesChannelSettings from '@/plugins/graphql/queries/v2/app-sales-channel-settings.gql';
import ProductDelete from '@/plugins/graphql/mutations/v2/ecommerce/product-delete.gql';
import ProductPublishUpdate from '@/plugins/graphql/mutations/v2/ecommerce/product-publish-update.gql';
import EcommerceProductListUpdate from '@/plugins/graphql/mutations/v2/ecommerce/product-list-update.gql';
import { successfulDestroy, successfulUpdate } from '@/helpers/mixins/LocalizationHelper';
import ShopifyProductSynced from '@/plugins/graphql/subscriptions/v2/shopify-product-synced.gql';
import { Getter, Mutation } from 'vuex-class';
import { EcommerceProductModel } from '@/helpers/entities/models/EcommerceProductModel';
import { omitBy, isNull, last } from 'lodash';
import BaseOverlayLoader from '@/components/v2/BaseOverlayLoader';
import AppVisibleProductsCount from '@/plugins/graphql/queries/v2/ecommerce/app-visible-products-count.gql';
import RouterParamsAppMixin from '@/helpers/vue_mixins/RouterParamsAppMixin';
import CleverTapEventsMixin from '@/helpers/vue_mixins/v3/CleverTapEventsMixin';
let ProductsManager = class ProductsManager extends mixins(FormMixin, PaginatorMixin, RouterParamsAppMixin, CleverTapEventsMixin) {
    constructor() {
        super(...arguments);
        this.products = null;
        this.selectedProductUuid = null;
    }
    fetchProducts() {
        this.$apollo.queries.products.refetch();
    }
    get title() {
        return this.$route.meta.title;
    }
    toggleConfirmation(uuid) {
        this.selectedProductUuid = uuid;
        this.showConfirmationDialog();
    }
    queryParams() {
        const cursorName = `${this.title.toLowerCase()}_cursor`;
        const cursorStorage = localStorage.getItem(cursorName);
        if (cursorStorage) {
            this.startCursor = last(JSON.parse(cursorStorage));
        }
        const params = omitBy({
            appUuid: this.appUuid,
            keyword: this.keyword,
            after: this.startCursor || null,
            first: this.all ? null : this.paginationLimit,
        }, isNull);
        if (this.categoryUuid) {
            return {
                ...params,
                categoryUuid: this.categoryUuid,
            };
        }
        return params;
    }
    goToProductEditPage(uuid) {
        this.productAction('START EDIT');
        this.$router.push({
            name: 'ecommerce-products-edit',
            params: {
                uuid,
            },
        });
    }
    goToProductDetailsPage(uuid) {
        this.$router.push({
            name: 'ecommerce-products-details',
            params: {
                uuid,
            },
        });
    }
    async updatePublishStatus(publish, productUuid) {
        this.requestOnFlight();
        await this.$apollo
            .mutate({
            mutation: ProductPublishUpdate,
            variables: {
                input: {
                    productUuid,
                    publish: publish || false,
                },
            },
        })
            .then(() => {
            const msg = successfulUpdate('Product Publish Status');
            this.successNotification(msg);
        })
            .catch((e) => {
            this.errorNotification(e.message);
        })
            .finally(() => {
            this.requestDone();
        });
    }
    async uponConfirm() {
        this.requestOnFlight();
        await this.delete()
            .then(() => {
            const msg = successfulDestroy('Product');
            this.successNotification(msg);
        })
            .catch((e) => {
            this.errorNotification(e.message);
        })
            .finally(() => {
            this.requestDone();
            this.hideConfirmationDialog();
        });
    }
    async delete() {
        const vm = this;
        if (!this.selectedProductUuid) {
            return;
        }
        await this.$apollo.mutate({
            mutation: ProductDelete,
            variables: {
                input: {
                    productUuid: this.selectedProductUuid,
                },
            },
            update: (cache, { data }) => {
                const { product } = data.ecommerceProductDelete;
                const queryOptions = {
                    query: EcommerceAppProducts,
                    variables: vm.queryParams(),
                };
                const { fetchAppEcommerceProducts } = cache.readQuery({
                    ...queryOptions,
                });
                const deletedProduct = (dp) => {
                    return dp.node.uuid !== product.uuid;
                };
                cache.writeQuery({
                    ...queryOptions,
                    data: {
                        fetchAppEcommerceProducts: {
                            ...fetchAppEcommerceProducts,
                            edges: fetchAppEcommerceProducts.edges.filter(deletedProduct),
                        },
                    },
                });
            },
            refetchQueries: [
                {
                    query: AppVisibleProductsCount,
                    variables: {
                        appUuid: vm.appUuid,
                    },
                },
            ],
        });
    }
    /**
     * Product Sequence Update Handler
     * @author Peter John Alvarado <peterjohn@gorated.ph>
     * @since Version {0.1.14}
     *
     * @param {string} product uuid
     * @param {string} new sortable position
     * @param {string} old sortable position
     *
     * @return {void} no return value
     */
    onPositionChanged(productUuid, newPos, oldPos) {
        const newPosition = newPos + 1;
        const oldPosition = oldPos + 1;
        const queryParams = this.queryParams();
        if (newPosition !== oldPosition) {
            this.$apollo.mutate({
                mutation: EcommerceProductListUpdate,
                variables: {
                    input: {
                        categoryUuid: this.categoryUuid,
                        ecommerceProductUuid: productUuid,
                        position: newPosition,
                    },
                },
                refetchQueries: [
                    {
                        query: EcommerceAppProducts,
                        variables: queryParams,
                    },
                ],
            });
        }
    }
};
__decorate([
    Getter('appUuid')
], ProductsManager.prototype, "appUuid", void 0);
__decorate([
    Mutation('showConfirmationDialog')
], ProductsManager.prototype, "showConfirmationDialog", void 0);
__decorate([
    Mutation('hideConfirmationDialog')
], ProductsManager.prototype, "hideConfirmationDialog", void 0);
__decorate([
    Prop({ type: String, default: null })
], ProductsManager.prototype, "categoryUuid", void 0);
__decorate([
    Prop({ type: Boolean, default: false })
], ProductsManager.prototype, "all", void 0);
__decorate([
    Prop({ type: String, default: null })
], ProductsManager.prototype, "keyword", void 0);
__decorate([
    Prop({ type: Number, default: 10 })
], ProductsManager.prototype, "paginationLimit", void 0);
ProductsManager = __decorate([
    Component({
        apollo: {
            products: {
                query: EcommerceAppProducts,
                variables() {
                    return this.queryParams();
                },
                fetchPolicy: 'cache-and-network',
                update(data) {
                    const products = data.fetchAppEcommerceProducts.edges;
                    const productNode = (product) => {
                        return new EcommerceProductModel(product.node);
                    };
                    return products.map(productNode);
                },
                result({ data }) {
                    if (data) {
                        const query = data.fetchAppEcommerceProducts;
                        this.totalCount = query.totalCount;
                        this.pageInfo = query.pageInfo;
                    }
                },
                subscribeToMore: {
                    document: ShopifyProductSynced,
                    variables() {
                        return {
                            ...this.queryParams(),
                            appUuid: this.baseAppUUID,
                        };
                    },
                    updateQuery(previousResult) {
                        // needed to refetch in order to fetch updated pageInfo and cursors
                        this.$apollo.queries.products.refetch();
                        return previousResult;
                    },
                },
            },
            appSalesChannelSettings: {
                query: AppSalesChannelSettings,
                variables() {
                    return {
                        appUuid: this.appUuid,
                    };
                },
                update(data) {
                    if (data) {
                        return data.fetchAppByUuid;
                    }
                },
            },
        },
        components: {
            ConfirmationDialog,
            BaseOverlayLoader,
        },
    })
], ProductsManager);
export default ProductsManager;
